import { useState } from "react";
import { Bell, CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "../../config/constants";

export function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setStatus("loading");
    setErrorMsg("");
    
    try {
      const res = await axios.post(`${API}/subscribers`, { email: email.trim() });
      if (res.data.already_subscribed) {
        setStatus("already");
      } else {
        setStatus("success");
        setEmail("");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-black via-zinc-900/30 to-black" data-testid="newsletter-section">
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-1.5 mb-6">
          <Bell size={14} className="text-red-400" />
          <span className="text-red-400 text-xs tracking-widest uppercase font-semibold">Never Miss a Drop</span>
        </div>
        
        <h2 className="text-white text-4xl sm:text-5xl font-bebas mb-3">
          Get Notified on <span className="text-cyan-400">New Drops</span>
        </h2>
        <p className="text-white/50 text-sm sm:text-base mb-8 max-w-lg mx-auto">
          Be the first to know when we drop new heat. Exclusive access, early drops, and fresh fits straight to your inbox.
        </p>

        {status === "success" ? (
          <div className="flex items-center justify-center gap-3 bg-green-500/10 border border-green-500/30 rounded-lg px-6 py-4" data-testid="newsletter-success">
            <CheckCircle size={20} className="text-green-400" />
            <span className="text-green-400 font-semibold">You're in! We'll hit you up when new drops land.</span>
          </div>
        ) : status === "already" ? (
          <div className="flex items-center justify-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg px-6 py-4" data-testid="newsletter-already">
            <CheckCircle size={20} className="text-amber-400" />
            <span className="text-amber-400 font-semibold">You're already on the list! Stay tuned.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="flex-1 px-5 py-3.5 bg-zinc-900 border border-white/15 rounded-lg text-white placeholder:text-white/30 focus:border-cyan-400 focus:outline-none transition-colors"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-8 py-3.5 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              data-testid="newsletter-submit-btn"
            >
              {status === "loading" ? (
                <><Loader2 size={18} className="animate-spin" /> Joining...</>
              ) : (
                "Notify Me"
              )}
            </button>
          </form>
        )}
        
        {status === "error" && (
          <p className="text-red-400 text-sm mt-3" data-testid="newsletter-error">{errorMsg}</p>
        )}
        
        <p className="text-white/25 text-xs mt-4">No spam. Just fresh drops.</p>
      </div>
    </section>
  );
}
